import { Navigate, Route, Routes } from 'react-router-dom';

import { FieldsFromHistoryPage } from './derived/FieldsFromHistoryPage';
import { AiCapabilitiesPage, AiCreditsPage, AiUsagePage } from './ai/AiManagementPages';
import { AllTabsPage } from './AllTabsPage';
import { AppShell } from './AppShell';
import { DeliveryHomePage } from './delivery/DeliveryHomePage';
import { HomePage } from './HomePage';
import { SellerHomePage } from './home/SellerHomePage';
import type { ObjectKey } from './navigation';
import { DELIVERY_HOME_ROUTE, LIBRARY_ROUTE, MOVED_ROUTES, MY_DASHBOARD_ROUTE, NEW_PRODUCT_ROUTE, OBJECTS, PRODUCT_CATALOG_ROUTE, REPORT_DASHBOARD_ROUTE, REPORTS_ROUTE, RESOURCING_ROUTE, SELLER_HOME_ROUTE, SETUP_ROUTES, WORK_ROUTE } from './navigation';
import { LibraryEntryPage } from './library/LibraryEntryPage';
import { LibraryPage } from './library/LibraryPage';
import { NotFoundPage } from './NotFoundPage';
import { OBJECT_DEFS } from './objects/objectDefs';
import { ObjectCloneView } from './objects/ObjectCloneView';
import { ObjectListPage } from './objects/ObjectListPage';
import { clonable } from './objects/routes';
import { ObjectRecordView } from './objects/ObjectRecordView';
import { ObjectRelatedListView } from './objects/ObjectRelatedListView';
import { NewProductPage } from './products/NewProductPage';
import { ProductCatalogPage } from './products/ProductCatalogPage';
import { ProfileProvider } from './profile/ProfileProvider';
import { SETTINGS_ROUTE } from './profile/routes';
import { ReportDashboardPage } from './reports/ReportDashboardPage';
import { ReportRunPage } from './reports/ReportPages';
import { ReportsListPage } from './reports/ReportsListPage';
import { ResourcingPage } from './resourcing/ResourcingPage';
import { SettingsPage } from './settings/SettingsPage';
import {
  SetupHomePage,
  SetupObjectDetailPage,
  SetupObjectsPage,
} from './setup/SetupPages';
import { PossibleDuplicatesPage } from './setup/PossibleDuplicatesPage';
import { RecycleBinPage } from './setup/RecycleBinPage';
import { SetupPartnerPage, SetupPartnersPage } from './setup/PartnersPage';
import { SetupUserRecordPage, SetupUsersPage } from './setup/SetupUsers';
import { TABS_ROUTE } from './tabs';
import { accessOf } from './auth/api';
import { authenticatedFetch } from './auth/authClient';
import { useCurrentUser } from './auth/AuthProvider';
import { WorkPage } from './work/WorkPage';
import { OAuthConsentPage, OAuthLoginPage } from './auth/OAuthPages';
import { ProposalPage } from './quotes/ProposalPage';

/** Sales Pulse, or My Delivery for a team-only person, who may not open Sales Pulse. */
function HomeRoute(): JSX.Element {
  return accessOf(useCurrentUser()) === 'all' ? <HomePage /> : <Navigate replace to={DELIVERY_HOME_ROUTE} />;
}

/** A partner's page, told the signed-in role: only an Admin changes the referral rate or reads its history (MOTHER-144). */
function PartnerRoute(): JSX.Element {
  const { role } = useCurrentUser();
  return <SetupPartnerPage fetcher={authenticatedFetch} role={role} />;
}

/** Setup → Possible Duplicates, told the signed-in role: only an Admin sees and merges duplicates (MOTHER-179). */
function PossibleDuplicatesRoute(): JSX.Element {
  const { role } = useCurrentUser();
  return <PossibleDuplicatesPage fetcher={authenticatedFetch} role={role} />;
}

/** A record page, told the signed-in role: only an Admin sees an account's or contact's Merged Records (MOTHER-180). */
function RecordRoute({ object }: { object: ObjectKey }): JSX.Element {
  const { role } = useCurrentUser();
  return <ObjectRecordView object={object} role={role} />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      {/* Neon Auth's OAuth provider sends MCP clients' sign-in here (auth/OAuthPages.tsx); outside the app shell. */}
      <Route path="/oauth/login" element={<OAuthLoginPage />} />
      <Route path="/oauth/consent" element={<OAuthConsentPage />} />
      <Route
        element={
          <ProfileProvider>
            <AppShell />
          </ProfileProvider>
        }
      >
        <Route path="/" element={<HomeRoute />} />
        {/* New products are set up end to end by a flow, not a form; a fixed path, so it wins over /products/:id. */}
        <Route path={NEW_PRODUCT_ROUTE} element={<NewProductPage />} />
        <Route path={PRODUCT_CATALOG_ROUTE} element={<ProductCatalogPage />} />
        {OBJECTS.map((o) => (
          // Keyed by object, so moving between lists starts each one fresh.
          <Route key={o.key} path={o.href} element={<ObjectListPage key={o.key} def={OBJECT_DEFS[o.key]} />} />
        ))}
        {OBJECTS.filter((o) => clonable(o.key)).map((o) => (
          // A new record on the record page (Clone); a fixed path, so it wins over /:id.
          <Route key={`${o.key}-new`} path={`${o.href}/new`} element={<ObjectCloneView key={o.key} object={o.key} />} />
        ))}
        {OBJECTS.map((o) => (
          <Route
            key={`${o.key}-record`}
            path={`${o.href}/:id`}
            element={<RecordRoute key={o.key} object={o.key} />}
          />
        ))}
        {OBJECTS.map((o) => (
          <Route
            key={`${o.key}-related`}
            path={`${o.href}/:id/related/:relation`}
            element={<ObjectRelatedListView key={o.key} object={o.key} />}
          />
        ))}
        {/* The workspace was its own page; its tabs are on the quote's record page now. */}
        <Route path="/quotes/:id/build" element={<Navigate to=".." relative="path" replace />} />
        <Route path="/quotes/:id/proposal" element={<ProposalPage />} />
        <Route path={RESOURCING_ROUTE} element={<ResourcingPage />} />
        <Route path={WORK_ROUTE} element={<WorkPage />} />
        <Route path={DELIVERY_HOME_ROUTE} element={<DeliveryHomePage />} />
        <Route path={SELLER_HOME_ROUTE} element={<SellerHomePage />} />
        <Route path={LIBRARY_ROUTE} element={<LibraryPage />} />
        <Route path={`${LIBRARY_ROUTE}/:id`} element={<LibraryEntryPage />} />
        <Route path={TABS_ROUTE} element={<AllTabsPage />} />
        <Route path={SETTINGS_ROUTE} element={<SettingsPage />} />
        <Route path={REPORTS_ROUTE} element={<ReportsListPage fetcher={authenticatedFetch} />} />
        <Route path={`${REPORTS_ROUTE}/new`} element={<ReportRunPage key="new" />} />
        <Route path={`${REPORTS_ROUTE}/:id`} element={<ReportRunPage />} />
        <Route path={MY_DASHBOARD_ROUTE} element={<ReportDashboardPage key="favorites" fetcher={authenticatedFetch} favorites />} />
        <Route path={`${REPORT_DASHBOARD_ROUTE}/:folderId`} element={<ReportDashboardPage fetcher={authenticatedFetch} />} />
        <Route path={`${REPORTS_ROUTE}/:id/edit`} element={<ReportRunPage key="edit" />} />
        <Route path={SETUP_ROUTES.home} element={<SetupHomePage />} />
        <Route path={SETUP_ROUTES.users} element={<SetupUsersPage />} />
        <Route path={`${SETUP_ROUTES.users}/:id`} element={<SetupUserRecordPage />} />
        <Route path={SETUP_ROUTES.aiUsage} element={<AiUsagePage />} />
        <Route path={SETUP_ROUTES.aiCredits} element={<AiCreditsPage />} />
        <Route path={SETUP_ROUTES.aiCapabilities} element={<AiCapabilitiesPage />} />
        <Route path={SETUP_ROUTES.objects} element={<SetupObjectsPage />} />
        <Route path={SETUP_ROUTES.fieldsFromHistory} element={<FieldsFromHistoryPage />} />
        <Route path={SETUP_ROUTES.recycleBin} element={<RecycleBinPage />} />
        <Route path={SETUP_ROUTES.possibleDuplicates} element={<PossibleDuplicatesRoute />} />
        <Route path={SETUP_ROUTES.partners} element={<SetupPartnersPage fetcher={authenticatedFetch} />} />
        <Route path={`${SETUP_ROUTES.partners}/:id`} element={<PartnerRoute />} />
        <Route path={`${SETUP_ROUTES.objects}/:key`} element={<SetupObjectDetailPage />} />
        {Object.entries(MOVED_ROUTES).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate replace to={to} />} />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
